import { useCallback, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useInterval } from 'react-use'
import { type MRT_ExpandedState } from 'material-react-table'

import type { Stores } from '../../util/store'
import { useQueueTasks } from '../../stores/queue-tasks-store'
import { useOrganizations } from '../../stores/organization-store'
import {
    QueueTaskDetailPanel,
    QueueTasksTable,
} from '../../components/tasks-table'

export interface QueueTasksPageProps {
    stores: Stores
}

const INTERVAL_DELAY = 30000

export const QueueTasksPage = observer(({ stores }: QueueTasksPageProps) => {
    const [pagination, setPagination] = useState({})
    const [filters, setFilters] = useState({})
    const [expanded, setExpanded] = useState<MRT_ExpandedState>({})

    const options = useMemo(() => {
        return {
            ...pagination,
            ...filters,
        }
    }, [pagination, filters])

    const tasks = useQueueTasks(stores.queueTasks, options)
    const organizations = useOrganizations(stores.organizations)

    useInterval(() => {
        if (!stores.queueTasks.isFetching) {
            void stores.queueTasks.list(options)
        }
    }, INTERVAL_DELAY)

    const renderDetailPanel = useCallback(
        (taskId: string) => {
            if (expanded === true || !expanded[taskId]) {
                return null
            }

            return (
                <QueueTaskDetailPanel
                    store={stores.queueTasks}
                    taskId={taskId}
                />
            )
        },
        [expanded, stores.queueTasks],
    )

    return (
        <div data-testid="tasks-page" style={{ display: 'contents' }}>
            <QueueTasksTable
                tasks={tasks}
                organizations={organizations}
                rowCount={stores.queueTasks.totalTasks}
                isLoading={
                    stores.queueTasks.isFetching && !stores.queueTasks.isFetched
                }
                isCreating={stores.queueTasks.isCreating}
                pagination={pagination}
                setPagination={setPagination}
                filters={filters}
                setFilters={setFilters}
                expanded={expanded}
                onExpandedChange={setExpanded}
                renderDetailPanel={renderDetailPanel}
                isPrimaryBackgroundColor={true}
            />
        </div>
    )
})
